<template>
  <section class="pu-modal">
    <div
      v-show="showTrigger"
      class="modal-trigger-wrapper"
      @click.stop.prevent.capture="openModal"
    >
      <slot name="modal-trigger"> Open modal </slot>
    </div>

    <client-only>
      <Teleport
        v-if="showModal || showModalState"
        :disabled="!usePortal"
        to="body"
      >
        <transition name="modal" tag="div">
          <div
            v-if="showModal || showModalState"
            class="modal-container"
            :class="[modalClass, { desktop: $isDesktop }]"
          >
            <div class="modal-header">
              <slot name="header">
                <a
                  class="close-button"
                  data-test="modal-close-button"
                  @click.prevent.stop="closeModal"
                >
                  <img
                    loading="lazy"
                    src="@/assets/images/x.svg"
                    width="14"
                    height="14"
                  />
                </a>
              </slot>
            </div>

            <div class="modal-body">
              <slot :close-modal="closeModal" />
            </div>

            <div class="modal-footer">
              <slot name="footer" :close-modal="closeModal" />
            </div>
          </div>
        </transition>

        <div class="pagemask" @click.prevent="closeModal" />
      </Teleport>
    </client-only>
  </section>
</template>

<script lang="ts">
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'

@Component({
  emits: [
    'change-show-modal-state',
    'closeModal',
    'closePUModal',
    'openModal',
    'openPUModal',
  ],
  setup() {
    const { finish } = useLoadingIndicator()
    return { finish }
  },
})
class PuModal extends Vue {
  finish!: () => void

  @Prop({ default: false })
  showModal: boolean

  showModalState = false

  @Prop({ default: true })
  showTrigger: boolean

  @Prop({ type: Boolean, default: false })
  usePortal: boolean

  @Prop({ required: false })
  modalClass?: string

  @Prop({ required: true })
  modalHash: string

  mounted() {
    const routeHash = this.$route.hash
    const hashes = routeHash.substring(1).split('&')
    if (routeHash !== '' && hashes.includes(this.modalHash)) {
      this.setModalStateOpen()
    }
  }

  setModalStateOpen() {
    this.showModalState = true
    this.$emit('change-show-modal-state', true)
  }

  setModalStateClosed() {
    this.showModalState = false
    this.$emit('change-show-modal-state', false)
  }

  @Watch('showModal')
  changeBodyAttribute(newValue: boolean) {
    newValue
      ? document.body.setAttribute('data-noscroll', 'true')
      : document.body.removeAttribute('data-noscroll')
  }

  @Watch('$route')
  routeChange(to, from) {
    if (to.path === from?.path && to.meta) {
      to.meta.scroll = false
    }

    const hash = to.hash || ''
    const hashes = hash.substring(1).split('&')
    if (hash !== '' && hashes.includes(this.modalHash)) {
      this.setModalStateOpen()
    } else {
      this.setModalStateClosed()
    }
  }

  openModal() {
    const currentHash = useRouter().currentRoute.value.hash.replace('#', '')
    const hashes = currentHash ? currentHash.split('&') : []

    if (hashes.includes(this.modalHash)) {
      return
    }

    const hash = '#' + (currentHash ? `${currentHash}&` : '') + this.modalHash
    void useRouter().push({
      hash,
      query: useRouter().currentRoute.value.query,
    })
    this.$emit('openModal', this.modalHash)
    this.$bubble('openPUModal', this.modalHash)
    this.finish()
  }

  closeModal() {
    this.$routerClient.getReferrer() ? useRouter().back() : this.applyModal()
    this.$emit('closeModal', this.modalHash)
    this.$bubble('closePUModal', this.modalHash)
    this.finish()
  }

  applyModal() {
    const currentHash = useRouter().currentRoute.value.hash
    const hashes = currentHash.substring(1).split('&')
    void useRouter().push({
      hash: hashes.filter((el) => el !== this.modalHash).join('&'),
      query: useRouter().currentRoute.value.query,
    })
  }

  beforeUnmount() {
    if (import.meta.client && document?.body) {
      const anyOtherModalOpen = !!document.querySelector('.modal-container')
      if (!anyOtherModalOpen) {
        document.body.removeAttribute('data-noscroll')
      }
    }
  }
}

export default toNative(PuModal)
</script>

<style scoped>
.pagemask {
  display: flex;
  justify-content: center;
}

.modal-container {
  background-color: #fff;
  border-radius: 0.6em;
  bottom: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  display: flex;
  flex-direction: column;
  height: auto;
  left: 3vw;
  margin: 0 auto;
  max-height: calc(100% - 6vw);
  min-height: 10em;
  min-width: 15em;
  overflow: hidden;
  position: fixed;
  right: 3vw;
  top: 3vw;
  transition: all 0.2s ease;
  width: 94vw;
  z-index: 98;
}

.modal-header,
.modal-footer {
  height: auto;
  position: relative;
  z-index: 99;
}

.modal-header {
  height: auto;
}

.modal-body {
  height: 100%;
  overflow-y: auto;
}

:deep(.close-button) {
  background: var(--colour-page-background);
  border-radius: 1.75em;
  border: none;
  box-shadow: none;
  display: block;
  height: 6em;
  line-height: 0;
  opacity: 1;
  padding: 0;
  position: absolute;
  right: -3.2em;
  top: -3.2em;
  width: 6em;
}

:deep(.close-button:hover, .close-button:focus-visible, .close-button:active) {
  background: #a0abb8;
  box-shadow: none;
  opacity: 1;
  transform: none;
  outline: none;
}

:deep(.close-button img) {
  bottom: 1.1em;
  height: auto;
  left: 1.1em;
  position: absolute;
  width: 0.85em;
}

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  -webkit-transform: scale(0.8);
  transform: scale(0.8);
}

/* desktop & tablet */
.modal-container.desktop {
  max-width: 26em;
  font-size: initial;
}
</style>
